<template>
  <div class="container px-4 pt-1">
    <Header
      :screenTitle="t('PAYJOY_CARD_VIEWS.SHIPPING_FORM_TITLE_2')"
      :navbarType="'goBack'"
      :showProgressBar="true"
      :progresPercentage="progressPercentage"
      @clicked="handleBackClick"
    />
    <ShippingAddressHeader :customTitle="props.customTitle" />

    <div>
      <div class="d-flex flex-column mt-1 mb-2 justify-content-center">
        <div>
          <TextInput
            :id="'autocomplete_mx'"
            :type="'text'"
            :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_STREET')"
            :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_STREET')"
            v-model:value="streetField"
            :errorMessage="streetNumberFieldError"
            :errorIcon="false"
            :highlight="highlightFields"
          />
        </div>
      </div>
      <div class="d-flex">
        <div class="field">
          <TextInput
            :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_STREET_NUMBER')"
            :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_STREET_NUMBER')"
            :fill="true"
            :max-length="5"
            :type="'number'"
            v-model:value="streetNumberField"
            :error="streetNumberFieldError !== ''"
            :errorMessage="streetNumberFieldError"
            :errorIcon="false"
            :highlight="highlightFields"
          />
        </div>
        <div class="field-attachment">
          <TextInput
            :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_POSTAL_CODE')"
            :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_POSTAL_CODE')"
            :fill="true"
            :max-length="5"
            :type="'tel'"
            :inputMode="'numeric'"
            :pattern="'[0-9]*'"
            v-model:value="postalCodeField"
            :errorIcon="false"
            :highlight="highlightFields"
            @input="validateManualZipCode"
          />
        </div>
      </div>
      <div class="d-flex">
        <div class="field">
          <TextInput
            :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_APARTMENT_LABEL')"
            :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_APARTMENT')"
            :fill="false"
            :max-length="15"
            v-model:value="apartmentField"
            :errorIcon="false"
            :highlight="highlightFields"
          />
        </div>
        <div class="field-attachment">
          <TextInput
            :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_FLOOR')"
            :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_FLOOR')"
            :fill="false"
            :max-length="15"
            :type="'number'"
            v-model:value="floorField"
          />
        </div>
      </div>
      <TextInput
        :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_NEIGHBORHOOD')"
        :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_NEIGHBORHOOD')"
        :fill="true"
        :max-length="25"
        v-model:value="neighborhoodField"
        :highlight="highlightFields"
      />
      <TextInput
        :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_CITY')"
        :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_CITY')"
        :fill="true"
        :max-length="25"
        v-model:value="cityField"
        :highlight="highlightFields"
        :error="cityFieldError !== ''"
        :errorMessage="cityFieldError"
      />
      <TextInput
        :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_REGION')"
        :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_REGION')"
        :fill="true"
        :max-length="35"
        v-model:value="regionField"
        :highlight="highlightFields"
      />
      <TextInput
        :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_NOTES')"
        :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_NOTES_EXPLAINER')"
        :fill="true"
        :max-length="30"
        v-model:value="notesField"
        @keyup.enter="handleNotesEnter"
      />
    </div>
    <div class="floating-btn">
      <Button
        id="btn-continue-1"
        class="text-start w-100 p-3"
        @click="onConfirmClick"
      >
        <div class="text-white text-center my-auto fw-bold btn-text">
          <span>{{ submitCta }}</span>
        </div>
      </Button>
      <Button
        v-if="!hideCancelButton"
        id="btn-continue-2"
        class="text-start w-100 p-3"
        @click="onCancelClick"
        :secondary="true"
      >
        <div class="text-center my-auto fw-bold btn-text">
          <span>{{ t("PAYJOY_CARD_VIEWS.BACK_TO_USER_DATA") }}</span>
        </div>
      </Button>
      <div>
        <DownUpModal
          v-if="showNotCoverageErrorModal"
          class="down-up-modal"
          :modalHeaderImage="
            useAsset('img/credit-line/credit-card/newShipment.png')
          "
          :modalHeaderTitle="
            t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_COVERAGE_ERROR_MODAL_TITLE')
          "
          :modalHeaderSubtitle="
            t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_COVERAGE_ERROR_MODAL_SUBTITLE')
          "
          :btnText="
            t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_COVERAGE_ERROR_MODAL_BUTTON')
          "
          @modalAction="enterNewAddress"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, onMounted, inject } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Button from "@/apps/credit-line/components/Button.vue";
import TextInput from "@/apps/credit-line/components/TextInput.vue";
import ShippingAddressHeader from "../components/ShippingAddressHeader.vue";
import { useShippingAddressForm } from "@/apps/credit-line/components/CreditCard/ShippingAddress/composables/useShippingAddressForm";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { Service } from "@/apps/credit-line/services/network.service";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { useStore } from "vuex";
import { Ref } from "vue";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";
import { useGoogleApiAutocompleteIntegration } from "@/apps/credit-line/components/CreditCard/ShippingAddress/composables/GoogleApiAutocompleteIntegration";
import Header from "@/shared-components/consumer/Header.vue";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";

interface Props {
  hideCancelButton?: boolean;
  customTitle?: string;
  customSubmitCta?: string;
  source?: string;
}

const props = withDefaults(defineProps<Props>(), {
  hideCancelButton: false,
  source: "",
});

const creditLineId = inject("creditLineId");
const deviceTag = inject("deviceTag");

const { t } = useI18n();

const store = useStore();

const {
  loadGoogleMapsScript,
  setupAutocomplete,
  components,
  isStreetNameAutoCompleteUsed,
  isProposedAddressSelected,
  isZipCodeAutoCompleteFilled,
  highlightFields,
} = useGoogleApiAutocompleteIntegration();

let startedFillout: Ref<boolean> = ref(false);
const CreditLineService = new Service();
const autocompleteSelectedStreetName = ref<string | null>(null);
const autocompleteSelectedStreetNumber = ref<string | null>(null);
const isFinalStreetNameManuallyFilled = ref<boolean>(false);
const isFinalStreetNumberManuallyFilled = ref<boolean>(false);
const progressPercentage = ref<number>(99);
const showNotCoverageErrorModal = ref<boolean>(false);
const logsData = ref<
  Record<string, string | number | boolean | null | undefined>
>({});

const emit = defineEmits<{
  (e: "onContinueClick", data: unknown): void;
  (e: "onCancelClick"): void;
  (e: "goBackClick", data: unknown): void;
}>();

const submitCta = props.customSubmitCta
  ? props.customSubmitCta
  : t("PAYJOY_CARD_VIEWS.SUBMIT");

const {
  streetField,
  streetFieldError,
  streetNumberField,
  streetNumberFieldError,
  apartmentField,
  floorField,
  postalCodeField,
  postalCodeFieldError,
  neighborhoodField,
  cityField,
  regionField,
  notesField,
  validateRequiredFields,
  getFormData,
  isZipCodeUserProvided,
  shippingAttemptId,
  cityFieldError,
} = useShippingAddressForm(CL_COUNTRY.MX, deviceTag?.value || "");

const validateManualZipCode = () => {
  isZipCodeUserProvided.value = true;
};

const handleBackClick = () => {
  emit("goBackClick", {});
};

const enterNewAddress = () => {
  showNotCoverageErrorModal.value = false;
  postalCodeField.value = "";
  streetField.value = "";
  streetNumberField.value = "";
  neighborhoodField.value = "";
  cityField.value = "";
  regionField.value = "";
  notesField.value = "";
  streetFieldError.value = "";
  streetNumberFieldError.value = "";
  postalCodeFieldError.value = "";
  cityFieldError.value = "";

  isZipCodeUserProvided.value = false;
  isStreetNameAutoCompleteUsed.value = false;
  isZipCodeAutoCompleteFilled.value = false;
  isFinalStreetNameManuallyFilled.value = false;
  isFinalStreetNumberManuallyFilled.value = false;
};

const validateCoverageZone = async (zipCode) => {
  const requestParams: {
    zipCode?: string;
    deviceTag: string;
  } = {
    zipCode: zipCode,
    deviceTag:
      deviceTag?.value || (AuthenticationHelper.getDeviceTag() as string),
  };

  if (props.source === "dashboardTestingTool") {
    if (postalCodeField.value == "07790") {
      return Promise.resolve();
    } else {
      showNotCoverageErrorModal.value = true;
      return Promise.resolve();
    }
  }

  try {
    const res = await CreditLineService.validateCoverageZone(requestParams);
    if (res.data && res.data.isZoneWithCoverage === true) {
      logCoverageDataAnalytics(res.data);

      // Set city field if empty
      if (!cityField.value || cityField.value.trim() === "") {
        cityField.value = res.data.city || "";
      }

      // Set neighborhood field if empty
      if (!neighborhoodField.value || neighborhoodField.value.trim() === "") {
        neighborhoodField.value = res.data.municipality || "";
      }
    } else if (res.data && res.data.isZoneWithCoverage === false) {
      showNotCoverageErrorModal.value = true;
    } else if (res.data && res.data.error) {
      showNotCoverageErrorModal.value = true;
    } else {
      showNotCoverageErrorModal.value = true;
    }
  } catch (err) {
    showNotCoverageErrorModal.value = true;
    DatadogLogger.Log(
      "Error validating shipping zip code",
      {
        deviceTag: deviceTag?.value,
        error: err.message,
      },
      LogType.ERROR
    );
    if (err.response && !err.response.data?.errorCode) {
      DatadogLogger.Log(
        "Error validating shipping zip code network error",
        {
          deviceTag: deviceTag?.value,
        },
        LogType.ERROR
      );
    }
  }
};

const handleNotesEnter = () => {
  if (canSubmitForm()) {
    onConfirmClick();
  }
};

const canSubmitForm = () => {
  validateRequiredFields();
  zipLengthValidation(postalCodeField.value);

  return !(
    streetFieldError.value ||
    postalCodeFieldError.value ||
    cityFieldError.value
  );
};

const onCancelClick = () => {
  emit("onCancelClick");
};

const logCoverageDataAnalytics = (coverageData) => {
  // Check and log if city data is missing
  if (!coverageData.city || coverageData.city.trim() === "") {
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.CcCityMissingOnZipCodeCoverageData,
      "CC_Details",
      "CcShippingFormSentLogs"
    );
  }

  // Check and log if neighborhood/municipality data is missing
  if (!coverageData.municipality || coverageData.municipality.trim() === "") {
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.CcNeighborhoodMissingOnZipCodeCoverageData,
      "CC_Details",
      "CcShippingFormSentLogs"
    );
  }
};

const onConfirmClick = async () => {
  await validateRequiredFields();
  zipLengthValidation(postalCodeField.value);

  if (
    streetFieldError.value ||
    postalCodeFieldError.value ||
    cityFieldError.value
  ) {
    return;
  }

  await validateCoverageZone(postalCodeField.value);

  if (showNotCoverageErrorModal.value) {
    return;
  }

  emit("onContinueClick", getFormData());

  logsData.value = {
    creditLineId: creditLineId?.value,
    shippingAttemptId: shippingAttemptId.value,
    isZipCodeUserProvided: isZipCodeUserProvided.value,
    isStreetNameAutoCompleteUsed: isStreetNameAutoCompleteUsed.value,
    isZipCodeAutoCompleteFilled: isZipCodeAutoCompleteFilled.value,
    isFinalStreetNameManuallyFilled: isFinalStreetNameManuallyFilled.value,
    isProposedAddressSelected: isProposedAddressSelected.value,
    isFinalStreetNumberManuallyFilled: isFinalStreetNumberManuallyFilled.value,
  };

  store.dispatch(
    "creditLine/addShipmentLog",
    "Logs of how user used the forms: " + JSON.stringify(logsData.value)
  );

  logAnalyticsEvent(
    AnalyticEvents.CreditLine.CcShippingFormSentLogs,
    "CC_Details",
    "CcShippingFormSentLogs",
    logsData.value
  );
};

const zipLengthValidation = (zipCode) => {
  if (zipCode.length !== 5 || !/^\d{5}$/.test(zipCode)) {
    postalCodeFieldError.value = t(
      "PAYJOY_CARD_VIEWS.MX_ADDRESS_FORM_ZIP_ERROR"
    );
  } else {
    postalCodeFieldError.value = "";
  }
};

watch(streetNumberField, (newVal) => {
  if (newVal !== "" && newVal != autocompleteSelectedStreetNumber.value) {
    isFinalStreetNumberManuallyFilled.value = true;
  }
});

watch(streetField, (newVal) => {
  if (newVal !== "" && !startedFillout.value) {
    startedFillout.value = true;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.CcShippingStartFillout,
      "CC_Details"
    );
    return;
  }
  if (
    autocompleteSelectedStreetName.value &&
    newVal !== autocompleteSelectedStreetName.value
  ) {
    isFinalStreetNameManuallyFilled.value = true;
    return;
  }
});

watch(postalCodeField, (newValue) => {
  zipLengthValidation(newValue);
});

watch(
  () => components,
  (newComponents) => {
    postalCodeField.value = newComponents.postalCode || "";
    streetField.value = newComponents.street || "";
    streetNumberField.value = newComponents.streetNumber || "";
    regionField.value = newComponents.region || "";
    neighborhoodField.value = newComponents.neighborhood || "";
    cityField.value = newComponents.city || "";
  },
  { deep: true }
);

onMounted(async () => {
  shippingAttemptId.value = "OLD" + creditLineId?.value + Date.now();

  try {
    await loadGoogleMapsScript();
    const input = document.getElementById("autocomplete_mx");

    setupAutocomplete(input, "MX");
  } catch (error) {
    console.error("Error loading Google Maps script:", error);
  }

  logAnalyticsEvent(
    AnalyticEvents.CreditLine.MxShippingFormLoaded,
    "CC_Details"
  );
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.field {
  width: 60%;
  margin-right: 5px;
}

.field-attachment {
  width: 60%;
}

.floating-btn {
  background-color: white;
  left: 0;
  bottom: 0;
  z-index: 200;
  height: 120px;
  width: 100%;
}

.error-label {
  color: $pj-color-red;
  font-size: 14px;
}

.input-wrapper {
  height: 50px;
  background-color: white;
  border: 1px solid $pj-color-light-gray;
  border-radius: 11px;
  padding: 5px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;

  &:focus-within {
    border: 1px solid $pj-color-black;
  }

  &.fill {
    width: 100%;
  }

  &.error {
    border: 1px solid $pj-color-red;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &.untouched {
    border: 1px solid $pj-color-light-green;
  }
}

.input-label {
  position: absolute;
  top: 2px;
  left: 20px;
  font-size: 12px;
  color: $pj-color-cool-gray;
}

.input-element {
  color: $pj-color-black;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  border: none;
  flex-grow: 1;
  height: 100%;
  width: 100%;

  &:focus-visible,
  &:focus,
  &:active {
    outline-width: 0;
  }

  &::placeholder {
    color: $pj-color-cool-gray;
  }

  &.with-label {
    padding-top: 15px;
  }

  &.shrink {
    flex-shrink: 20;
  }
}
</style>
